import { AGENT_TYPES as AGENT_TYPE_VALUES } from '@aurora/contracts';
import { ApiError } from '../../lib/api';
import { shortDate } from '../../lib/format';
import type { AgentType, Membership } from './types';

/**
 * Words for states, kept in one place so the list, the panel and the groups
 * say the same thing about the same person.
 */

type Tone = 'neutral' | 'accent' | 'positive' | 'caution' | 'critical' | 'informative';

/**
 * Words for each agent type. A `Record` over the contract's type, so a type
 * added there fails to compile here until it has words — rather than being
 * missing from the menu, or offered as a raw key.
 */
const AGENT_TYPE_WORDS: Record<AgentType, { label: string; hint: string }> = {
  full_time: { label: 'Full-time', hint: 'Works the desk every day.' },
  occasional: { label: 'Occasional', hint: 'Helps out now and then.' },
};

/** The agent types to offer, in the contract's order: exactly what the server accepts. */
export const AGENT_TYPES: Array<{ value: AgentType; label: string; hint: string }> = AGENT_TYPE_VALUES.map(
  (value) => ({ value, ...AGENT_TYPE_WORDS[value] }),
);

export function agentTypeLabel(type: AgentType | null | undefined): string {
  return type === 'occasional' ? 'Occasional agent' : 'Agent';
}

export const MEMBERSHIPS: Array<{ value: Membership; label: string; hint: string }> = [
  { value: 'member', label: 'Member', hint: 'Sees the queue and is given tickets.' },
  { value: 'lead', label: 'Lead', hint: 'A member who also leads the group.' },
  { value: 'observer', label: 'Observer', hint: 'Sees the queue, but is never given tickets automatically.' },
];

export function membershipLabel(membership: string): string {
  return MEMBERSHIPS.find((m) => m.value === membership)?.label ?? membership;
}

/**
 * The account's state, as a badge. Plain "active" needs no badge.
 *
 * "Never signed in" is only said of agents: most requesters only ever email
 * the desk, so on them it would be on nearly every row and mean nothing,
 * while an agent who has never signed in has not started working.
 */
export function accountState(person: {
  is_active: boolean;
  is_agent: boolean;
  has_logged_in: boolean;
  merged_into_id: number | null;
  merged_into_name: string | null;
}): { label: string; tone: Tone } | null {
  if (person.merged_into_id !== null) {
    return { label: `Merged into ${person.merged_into_name ?? 'another account'}`, tone: 'neutral' };
  }
  if (!person.is_active) return { label: 'Deactivated', tone: 'neutral' };
  if (person.is_agent && !person.has_logged_in) return { label: 'Never signed in', tone: 'informative' };
  return null;
}

/** Where an account came from, in words rather than the stored code. */
export function sourceLabel(source: string | null): string | null {
  if (!source) return null;
  if (source === 'email') return 'Created from an email';
  if (source.startsWith('sso:')) return `Created at first sign-in (${source.slice(4)})`;
  if (source === 'csv') return 'Imported';
  return `From ${source}`;
}

/** "Available", "Away until 6 Oct, 14:00 · Annual leave", "Away · Sick". */
export function availabilitySummary(agent: {
  is_available: boolean | null;
  unavailable_until: string | null;
  unavailable_reason?: string | null;
}): { label: string; away: boolean } {
  if (agent.is_available !== false) return { label: 'Available', away: false };
  const until = agent.unavailable_until ? `Away until ${shortDate(agent.unavailable_until)}` : 'Away';
  return { label: agent.unavailable_reason ? `${until} · ${agent.unavailable_reason}` : until, away: true };
}

/**
 * The server's own words for a failure.
 *
 * The shared toast replaces any 403 with a generic "you don't have
 * permission", which is right for most screens and wrong for this one: here
 * the refusal is the explanation ("You can't deactivate Sam: they have access
 * you don't"), and somebody told only "no" will try again.
 */
export function explain(error: unknown): string {
  if (error instanceof ApiError) {
    const first = error.details?.[0];
    if (error.code === 'validation_failed' && first) return first.message;
    return error.message;
  }
  return 'Something went wrong. Please try again.';
}

/** A person's name, or their address when the name is empty. */
export function displayName(person: { full_name?: string | null; email?: string | null }): string {
  return person.full_name?.trim() || person.email || 'Unnamed';
}
